import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { useGetChannelsPaginatedFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-channels-paginated.form-validation';
import { GetChannelsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-channels-paginated.model';
import { DateRangeFilterModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/core/src/models/inputs/date-range-filter.model';
import { Controller, useFormContext } from 'react-hook-form';

import { ChargePointChannelOcppStatusFilterController } from '@app/feature/charge-point/charge-point-channel-ocpp-status-filter/charge-point-channel-ocpp-status-filter.controller';
import { ChargePointIsMaintenanceFilterController } from '@app/feature/charge-point/charge-point-is-maintenance-filter/charge-point-is-maintenance-filter.controller';
import { ChargePointIsOnlineFilterController } from '@app/feature/charge-point/charge-point-is-online-filter/charge-point-is-online-filter.controller';
import { CustomerFilterController } from '@app/feature/customer/customer-filter/customer-filter.controller';
import { LocationFilterController } from '@app/feature/location/location-filter/location-filter.controller';
import { NetworkFilterController } from '@app/feature/network/network-filter/network-filter.controller';
import { TariffFilterController } from '@app/feature/tariff/tariff-filter/tariff-filter.controller';
import { toClearableFieldValue } from '@shared/form/to-clearable-field-value.util';

import { ChannelListLastMessageReceivedDateFilterController } from '../../channel-list-last-message-received-date-filter/channel-list-last-message-received-date-filter.controller';

const ChannelListFilterFormStep1IsOnlineFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { isOnline } = useGetChannelsPaginatedFormValidation();

  // RENDER //////////////////////////////////////////////////////////////////

  return (
    <>
      <Controller
        name="isOnline"
        control={control}
        rules={isOnline}
        render={({ field }) => {
          return (
            <ChargePointIsOnlineFilterController
              selectedItem={field.value}
              onChange={(value) => field.onChange(toClearableFieldValue(value))}
            />
          );
        }}
      />
      <Divider />
    </>
  );
};

const ChannelListFilterFormStep1IsMaintenanceFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { control } = useFormContext<GetChannelsPaginatedModel>();
    const { isMaintenance } = useGetChannelsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="isMaintenance"
          control={control}
          rules={isMaintenance}
          render={({ field }) => {
            return (
              <ChargePointIsMaintenanceFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChannelListFilterFormStep1LastMessageReceivedDateFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { control } = useFormContext<GetChannelsPaginatedModel>();
    const { lastMessageReceivedDate } = useGetChannelsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="lastMessageReceivedDate"
          control={control}
          rules={lastMessageReceivedDate}
          render={({ field }) => {
            // The SDK field also allows an exact-match scalar date, but this
            // filter UI only ever reads/writes the min/max range shape.
            const value = field.value as DateRangeFilterModel | undefined;

            return (
              <ChannelListLastMessageReceivedDateFilterController
                value={value}
                onChange={field.onChange}
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChannelListFilterFormStep1StatusFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  // const { t } = useTranslation();
  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { channelOcppStatus } = useGetChannelsPaginatedFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Controller
        name="channelOcppStatus"
        control={control}
        rules={channelOcppStatus}
        render={({ field /* , fieldState: { error } */ }) => {
          const values = [];

          if (Array.isArray(field.value)) {
            values.push(...field.value);
          } else if (field.value) {
            values.push(field.value);
          }

          return (
            <ChargePointChannelOcppStatusFilterController
              selectedItemIds={values}
              onChange={(value) => field.onChange(value)}
            />
          );
        }}
      />
      <Divider />
    </>
  );
};

const ChannelListFilterFormStep1CustomerIdFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { customerId } = useGetChannelsPaginatedFormValidation();

  // RENDER //////////////////////////////////////////////////////////////////

  return (
    <>
      <Controller
        name="customerId"
        control={control}
        rules={customerId}
        render={({ field /* , fieldState: { error } */ }) => {
          const values = [];

          if (Array.isArray(field.value)) {
            values.push(...field.value);
          } else if (field.value) {
            values.push(field.value);
          }

          return (
            <CustomerFilterController
              selectedItemIds={values}
              onChange={(value) => field.onChange(value)}
            />
          );
        }}
      />
      <Divider />
    </>
  );
};

const ChannelListFilterFormStep1LocationIdFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  // const { t } = useTranslation();
  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { locationId } = useGetChannelsPaginatedFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Controller
        name="locationId"
        control={control}
        rules={locationId}
        render={({ field /* , fieldState: { error } */ }) => {
          const values = [];

          if (Array.isArray(field.value)) {
            values.push(...field.value);
          } else if (field.value) {
            values.push(field.value);
          }

          return (
            <LocationFilterController
              selectedItemIds={values}
              onChange={(value) => field.onChange(value)}
            />
          );
        }}
      />
      <Divider />
    </>
  );
};

const ChannelListFilterFormStep1NetworkIdFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { networkId } = useGetChannelsPaginatedFormValidation();

  // RENDER //////////////////////////////////////////////////////////////////

  return (
    <>
      <Controller
        name="networkId"
        control={control}
        rules={networkId}
        render={({ field /* , fieldState: { error } */ }) => {
          const values = [];

          if (Array.isArray(field.value)) {
            values.push(...field.value);
          } else if (field.value) {
            values.push(field.value);
          }

          return (
            <NetworkFilterController
              selectedItemIds={values}
              onChange={(value) => field.onChange(value)}
            />
          );
        }}
      />
      <Divider />
    </>
  );
};

const ChannelListFilterFormStep1TariffIdFragment = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  // const { t } = useTranslation();
  const { control } = useFormContext<GetChannelsPaginatedModel>();
  const { tariffId } = useGetChannelsPaginatedFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Controller
      name="tariffId"
      control={control}
      rules={tariffId}
      render={({ field /* , fieldState: { error } */ }) => {
        const values = [];

        if (Array.isArray(field.value)) {
          values.push(...field.value);
        } else if (field.value) {
          values.push(field.value);
        }

        return (
          <TariffFilterController
            selectedItemIds={values}
            onChange={(value) => field.onChange(value)}
          />
        );
      }}
    />
  );
};

export const ChannelListFilterFormStep1Controller = (): React.JSX.Element => {
  // RENDER //////////////////////////////////////////////////////////////////

  return (
    <Stack
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      flex={1}
      gap={GapEnum.M}>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.M}>
        <ChannelListFilterFormStep1IsOnlineFragment />
        <ChannelListFilterFormStep1IsMaintenanceFragment />
        <ChannelListFilterFormStep1LastMessageReceivedDateFragment />
        <ChannelListFilterFormStep1StatusFragment />
        <ChannelListFilterFormStep1CustomerIdFragment />
        <ChannelListFilterFormStep1LocationIdFragment />
        <ChannelListFilterFormStep1NetworkIdFragment />
        <ChannelListFilterFormStep1TariffIdFragment />
      </Stack>
    </Stack>
  );
};
